import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ExternalLink,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
  Users,
} from "lucide-react";

import EmployeeEditor from "../../components/EmployeeEditor";
import StatusPill from "../../components/StatusPill";
import { ApiError, api } from "../../lib/api";
import { timeAgo } from "../../lib/format";
import type {
  CurrentUser,
  EmployeeListResponse,
  EmployeeSummary,
} from "../../lib/types";

const EMPTY_NEW: Parameters<typeof EmployeeEditor>[0]["employee"] = {
  oracle_id: "",
  first: "",
  middle: "",
  last: "",
  full_name: "",
  nationality: "",
  primary_phone: "",
  email: "",
  home: { country: "", city: "", address: "" },
  org: {
    unit_id: "",
    unit_name: "",
    function: "",
    title: "",
    manager_oracle_id: "",
    manager_name: "",
  },
  emergency_contact_name: "",
  emergency_contact_relation: "",
  red_flags: [],
};

export default function EmployeesAdmin() {
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => api.get<CurrentUser>("/api/me"),
  });
  const isSuperAdmin = me.data?.role === "super_admin";

  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<EmployeeSummary | null>(null);
  const [confirmDel, setConfirmDel] = useState<EmployeeSummary | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const employees = useQuery({
    queryKey: ["employees-admin", search],
    queryFn: () =>
      api.get<EmployeeListResponse>(
        `/api/employees?${new URLSearchParams({
          ...(search ? { search } : {}),
          limit: "2000",
        })}`,
      ),
  });

  const items = useMemo(() => {
    const arr = employees.data?.items ?? [];
    return [...arr].sort((a, b) =>
      a.full_name.localeCompare(b.full_name, undefined, { sensitivity: "base" }),
    );
  }, [employees.data]);

  const del = useMutation({
    mutationFn: (oracleId: string) =>
      api.delete<{ ok: boolean }>(`/api/employees/${encodeURIComponent(oracleId)}`),
    onSuccess: (_data, oracleId) => {
      qc.invalidateQueries({ queryKey: ["employees-admin"] });
      qc.invalidateQueries({ queryKey: ["employees"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      setConfirmDel(null);
      setToast(`Removed ${oracleId}`);
      setTimeout(() => setToast(null), 4000);
    },
    onError: (e: Error) => {
      const msg =
        e instanceof ApiError ? `Delete failed: ${e.message}` : e.message;
      setToast(msg);
      setTimeout(() => setToast(null), 5000);
    },
  });

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-100 inline-flex items-center gap-2">
            <Users size={20} /> Employees
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Manage the full roster — add, edit, remove people from the dashboard.
          </p>
        </div>
        {isSuperAdmin && (
          <button
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-sky-500 hover:bg-sky-400 text-white px-3 py-2 text-sm font-medium shadow"
          >
            <Plus size={14} /> Add employee
          </button>
        )}
      </div>

      <div className="rounded-xl bg-[var(--color-surface)]/60 ring-1 ring-[var(--color-border)] p-2 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search
            size={14}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, oracle id…"
            className="w-full rounded-md bg-slate-900/80 ring-1 ring-slate-700 pl-8 pr-3 py-2 text-sm text-slate-100 outline-none focus:ring-sky-500"
          />
        </div>
        <span className="text-xs text-slate-400 px-2">
          {employees.isPending
            ? "Loading…"
            : `${items.length} employee${items.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {toast && (
        <div className="rounded-md bg-sky-500/10 ring-1 ring-sky-400/30 px-3 py-2 text-sm text-sky-100">
          {toast}
        </div>
      )}

      <div className="rounded-xl bg-[var(--color-surface)]/40 ring-1 ring-[var(--color-border)] overflow-hidden">
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/60 text-slate-400 text-xs uppercase tracking-wide">
              <tr>
                <Th>Name</Th>
                <Th>Oracle ID</Th>
                <Th>Title / Unit</Th>
                <Th>Phone</Th>
                <Th>Email</Th>
                <Th>Nationality</Th>
                <Th>Status</Th>
                <Th>Last seen</Th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/80">
              {items.map((e) => (
                <tr key={e.oracle_id} className="hover:bg-slate-800/40">
                  <td className="px-3 py-2 text-slate-100">
                    <Link
                      to={`/employees/${encodeURIComponent(e.oracle_id)}`}
                      className="hover:text-sky-300 inline-flex items-center gap-1.5"
                    >
                      {e.full_name}
                      {e.red_flag_count > 0 && (
                        <AlertTriangle size={12} className="text-amber-400" />
                      )}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-slate-400 tabular-nums">
                    {e.oracle_id}
                  </td>
                  <td className="px-3 py-2 text-slate-300">
                    <div>{e.title || "—"}</div>
                    <div className="text-xs text-slate-500 truncate max-w-[220px]">
                      {e.org_unit || ""}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-400 tabular-nums">
                    {e.primary_phone_masked || "—"}
                  </td>
                  <td className="px-3 py-2 text-slate-400 truncate max-w-[200px]">
                    {e.email || "—"}
                  </td>
                  <td className="px-3 py-2 text-slate-400">
                    {e.nationality || "—"}
                  </td>
                  <td className="px-3 py-2">
                    <StatusPill status={e.current.status} />
                  </td>
                  <td className="px-3 py-2 text-slate-500 text-xs">
                    {timeAgo(e.current.last_seen_at)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1 justify-end">
                      <Link
                        to={`/employees/${encodeURIComponent(e.oracle_id)}`}
                        className="p-1.5 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-800"
                        title="Open profile"
                      >
                        <ExternalLink size={14} />
                      </Link>
                      {isSuperAdmin && (
                        <>
                          <button
                            onClick={() => setEditing(e)}
                            className="p-1.5 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-800"
                            title="Edit"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => setConfirmDel(e)}
                            className="p-1.5 rounded-md text-slate-400 hover:text-red-300 hover:bg-red-500/10"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <ul className="md:hidden divide-y divide-slate-800/80">
          {items.map((e) => (
            <li key={e.oracle_id} className="p-3">
              <div className="flex items-start justify-between gap-3">
                <Link
                  to={`/employees/${encodeURIComponent(e.oracle_id)}`}
                  className="flex-1 min-w-0"
                >
                  <div className="text-slate-100 font-medium truncate inline-flex items-center gap-1.5">
                    {e.full_name}
                    {e.red_flag_count > 0 && (
                      <AlertTriangle size={12} className="text-amber-400" />
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {e.title || e.org_unit || "—"}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
                    <StatusPill status={e.current.status} />
                    <span className="tabular-nums">{e.oracle_id}</span>
                    {e.primary_phone_masked && (
                      <span className="tabular-nums">{e.primary_phone_masked}</span>
                    )}
                  </div>
                </Link>
                {isSuperAdmin && (
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => setEditing(e)}
                      className="p-2 rounded-md text-slate-300 hover:bg-slate-800"
                      aria-label="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => setConfirmDel(e)}
                      className="p-2 rounded-md text-red-300 hover:bg-red-500/10"
                      aria-label="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>

        {!employees.isPending && items.length === 0 && (
          <div className="text-sm text-slate-400 px-4 py-8 text-center">
            No employees match the current filters.
          </div>
        )}
      </div>

      {creating && isSuperAdmin && (
        <EmployeeEditor
          mode="create"
          employee={EMPTY_NEW}
          onClose={() => setCreating(false)}
        />
      )}

      {editing && isSuperAdmin && (
        <EmployeeEditor
          employee={{
            oracle_id: editing.oracle_id,
            full_name: editing.full_name,
            nationality: editing.nationality ?? null,
            primary_phone: null,
            email: editing.email ?? null,
            home: {
              country: editing.home?.country ?? null,
              city: editing.home?.city ?? null,
              address: editing.home?.address ?? null,
              lat: editing.home?.lat ?? null,
              lng: editing.home?.lng ?? null,
              lat_lng_manual: editing.home?.lat_lng_manual ?? false,
            },
            org: {
              unit_id: editing.org_unit_id ?? null,
              unit_name: editing.org_unit ?? null,
              function: editing.org_function ?? null,
              title: editing.title ?? null,
            },
            emergency_contact_name: null,
            emergency_contact_relation: null,
            red_flags: editing.red_flags ?? [],
            current: { status: editing.current.status },
          }}
          onClose={() => setEditing(null)}
        />
      )}

      {confirmDel && isSuperAdmin && (
        <ConfirmDelete
          employee={confirmDel}
          busy={del.isPending}
          onCancel={() => setConfirmDel(null)}
          onConfirm={() => del.mutate(confirmDel.oracle_id)}
        />
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 text-left font-medium">{children}</th>;
}

function ConfirmDelete({
  employee,
  busy,
  onCancel,
  onConfirm,
}: {
  employee: EmployeeSummary;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-[var(--color-surface)] ring-1 ring-[var(--color-border)] p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 text-red-300">
          <Trash2 size={18} />
          <h2 className="text-lg font-semibold text-slate-100">
            Delete employee?
          </h2>
        </div>
        <p className="mt-3 text-sm text-slate-300">
          Permanently remove{" "}
          <span className="font-medium text-slate-100">{employee.full_name}</span>{" "}
          (Oracle ID{" "}
          <span className="tabular-nums text-slate-400">{employee.oracle_id}</span>
          )? This will also delete their family entries, travel events and
          check-in history. This cannot be undone.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-slate-300 hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-red-500 hover:bg-red-400 disabled:opacity-50 text-white text-sm font-medium"
          >
            {busy ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Trash2 size={14} />
            )}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
